import React, { lazy, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { UptimeTheme, UptimeCustomLayout } from '@uptime/uptime-component';
import { ThemeProvider } from 'styled-components';
import GlobalStyle from 'utils/globalStyle';
import App from './App';
import { CONFIG } from 'constants/index';
import { AppService } from 'services';
import TeamsAuthContainer from 'containers/TeamsAuthContainer';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from 'config/appInsight';
import lumosTheme from 'utils/lumosTheme';
import {
  FONT_DEFAULT,
  FONT_FACES_DEFAULT,
  loadFonts,
  styleIdFontSetting,
} from 'assets/fonts/fonts';
import useApplyCustomLayout from 'hooks/useApplyCustomLayout';
import { ChatWidgetContainer } from 'containers/ChatWidgetContainer';

const MaintenancePage = lazy(() => import('pages/MaintenancePage'));

const AppContainer = (props: any) => {
  const { generalConfig } = useSelector(
    (state: RootState) => state.configReducer,
  );
  const { currentUser } = useSelector(
    (state: RootState) => state.baseReducer,
  );

  const [theme, setTheme] = useState({ ...lumosTheme } as any);

  //Invoke custom configs for customer layout, theme and component
  const { customLayout } = useApplyCustomLayout();
  const { isUseCustomTheme } = customLayout;

  useEffect(() => {
    (async () => {
      if (isUseCustomTheme) {
        const customTheme = await AppService.getJsonConfig('theme');
        setTheme({ ...theme, ...customTheme });
      }
    })();
  }, [isUseCustomTheme]);

  useEffect(() => {
    if(!currentUser || !generalConfig) return;
    const getCustomTheme = AppService.getValueConfigAzure(
      CONFIG.CUSTOM_THEME.KEY,
      CONFIG.CUSTOM_THEME.LABEL,
    );
    const getCustomAlignmentSetting = generalConfig?.isUseDynamicAlignmentCenter ? 
    AppService.getValueConfigAzure(
      CONFIG.UPTIME_DYNAMIC_ALIGN_CENTER.KEY,
      CONFIG.UPTIME_DYNAMIC_ALIGN_CENTER.LABEL,
    )
    : Promise.resolve({ status: 'rejected' });

    const getCustomFont = AppService.getValueConfigAzure(
      CONFIG.UPTIME_FONT_SETTING.KEY,
      CONFIG.UPTIME_FONT_SETTING.LABEL,
    );

    Promise.allSettled([
      getCustomTheme, 
      getCustomAlignmentSetting,
      getCustomFont
    ]).then(val => {
      const newTheme = {
        ...theme,
      };
      if(val[1]?.status === 'fulfilled' && val[1]?.value) {
        const dataAlignmentSetting = val[1]?.value;
        newTheme.uptimeDynamicAlignCenter = {
          ...newTheme.uptimeDynamicAlignCenter,
          ...dataAlignmentSetting.uptimeDynamicAlignCenter
        };
      }

      if (val[2]?.status === 'fulfilled' && val[2]?.value) {
        const dataFontSetting = val[2]?.value;
        
        if(dataFontSetting?.uptimeConfigPrimaryFont){
          const {
            fontName: fontNamePrimary,
            url: fontUrl,
          } = dataFontSetting?.uptimeConfigPrimaryFont;

          const isCheckFontData = !!fontUrl && !!fontNamePrimary;

          newTheme.uptimeFonts = {
            ...newTheme.uptimeFonts,
            primary: isCheckFontData ? fontNamePrimary : FONT_DEFAULT,
          };

          loadFonts(
            isCheckFontData
              ? `
          @font-face {
            font-family: '${fontNamePrimary}';
            src: url('${fontUrl}');
            font-weight: normal;
            font-style: normal;
          }
        `
              : FONT_FACES_DEFAULT,
            styleIdFontSetting,
          );
        }
        
        newTheme.uptimeDynamicFontSize = {
          ...newTheme.uptimeDynamicFontSize,
          ...dataFontSetting.uptimeDynamicFontSize,
        };
      }

      if (val[0]?.status === 'fulfilled' && val[0]?.value) {
        Object.assign(newTheme, val[0].value);
      }

      setTheme(newTheme);
    });

  }, [generalConfig?.isUseDynamicAlignmentCenter, currentUser, generalConfig]);

  return (
    <AppInsightsContext.Provider value={reactPlugin}>
      <ThemeProvider theme={theme}>
        <UptimeCustomLayout customLayoutConfig={customLayout}>
          <UptimeTheme theme={theme}>
            <GlobalStyle />
            <ChatWidgetContainer>
              <TeamsAuthContainer>
                {!generalConfig.isOutage? <App /> : <MaintenancePage />}
              </TeamsAuthContainer>
            </ChatWidgetContainer>
          </UptimeTheme>
        </UptimeCustomLayout>
      </ThemeProvider>
    </AppInsightsContext.Provider>
  );
};
export default AppContainer;
